import moment from 'moment-timezone'
import { getProgramDatesRange } from '../../utils/date'
import {
  getExperienceScenario,
  getParticipantCount,
  normalizeReadiness,
} from '../Quests/experienceStatus'

/** Cards / attention rows shown on Home (v7 shows the three nearest). */
export const HOME_EXPERIENCE_CARD_LIMIT = 3
export const HOME_ATTENTION_LIMIT = 3

const ART_TONES = ['a', 'b', 'c']

export const getArtToneClass = (index) =>
  `experience-home-art-tone-${ART_TONES[index % ART_TONES.length]}`

export const getExperienceDisplayName = (quest) => {
  const name = String(quest?.name || quest?.title || '').trim()
  return name || 'Untitled experience'
}

const SCENARIO_BADGE = {
  live: { label: 'In progress', className: 'badge-run' },
  upcoming: { label: 'Upcoming', className: 'badge-soon' },
  draft: { label: 'Draft', className: 'badge-draft' },
  completed: { label: 'Completed', className: 'badge-done' },
  new: { label: 'Draft', className: 'badge-draft' },
}

export const getScenarioBadge = (scenario) =>
  SCENARIO_BADGE[scenario] || SCENARIO_BADGE.draft

/**
 * v7 `.exp-date` — "21–30 Aug 2026" / "26 Sep – 4 Oct 2026", plus the
 * progress suffix ("Day 6 of 10" while live, "31 days away" before departure).
 */
export const getHomeDateLine = (quest, scenario) => {
  const { start, end } = getProgramDatesRange(quest)
  if (!start && !end) {
    return null
  }
  const from = start ? moment.utc(start) : null
  const to = end ? moment.utc(end) : from

  let range
  if (from && to && from.isSame(to, 'day')) {
    range = from.format('D MMM YYYY')
  } else if (from && to && from.isSame(to, 'month')) {
    range = `${from.format('D')}–${to.format('D MMM YYYY')}`
  } else if (from && to && from.isSame(to, 'year')) {
    range = `${from.format('D MMM')} – ${to.format('D MMM YYYY')}`
  } else if (from && to) {
    range = `${from.format('D MMM YYYY')} – ${to.format('D MMM YYYY')}`
  } else {
    range = (from || to).format('D MMM YYYY')
  }

  const today = moment.utc().startOf('day')
  if (scenario === 'live' && from && to) {
    const day = today.diff(from, 'days') + 1
    const total = to.diff(from, 'days') + 1
    if (day > 0 && total > 0) {
      return `${range} · Day ${day} of ${total}`
    }
  }
  if (scenario === 'upcoming' && from) {
    const away = from.diff(today, 'days')
    if (away > 0) {
      return `${range} · ${away} day${away === 1 ? '' : 's'} away`
    }
  }
  return range
}

/** v7 `.exp-type` — "Quest · Justin Milano" / "Quest · Not published". */
export const getHomeTypeLine = (quest, scenario) => {
  const type = quest?.programType || 'Experience'
  if (scenario === 'draft' || scenario === 'new') {
    return `${type} · Not published`
  }
  const leader =
    quest?.questLeaderName ||
    quest?.leaderName ||
    (Array.isArray(quest?.questLeadersEmails) && quest.questLeadersEmails[0]
      ? quest.questLeadersEmails[0]?.value ||
        quest.questLeadersEmails[0]?.email ||
        quest.questLeadersEmails[0]
      : null)
  return leader ? `${type} · ${leader}` : type
}

/**
 * v7 `.exp-home-meta` — "13 / 16 participants" plus a readiness warning.
 * Only renders what the quest response actually carries.
 */
export const getHomeMetaItems = (quest) => {
  const items = []
  const participants = getParticipantCount(quest)
  const capacity = Number(quest?.groupSize)
  if (participants != null) {
    items.push({
      key: 'participants',
      value:
        !Number.isNaN(capacity) && capacity > 0
          ? `${participants} / ${capacity}`
          : String(participants),
      label: 'participants',
    })
  }
  const issues = countReadinessIssues(quest)
  if (issues > 0) {
    items.push({
      key: 'issues',
      value: String(issues),
      label: `prep issue${issues === 1 ? '' : 's'}`,
      warn: true,
    })
  }
  return items
}

const readinessIssueCount = (item) => {
  if (!item || item.ok === true || item.complete === true) {
    return 0
  }
  const n = item.issues ?? item.count ?? item.missing
  const parsed = Number(n)
  return Number.isNaN(parsed) ? 0 : parsed
}

/** Total open readiness items, when the API sends a `readiness` object. */
export const countReadinessIssues = (quest) => {
  const r = normalizeReadiness(quest?.readiness)
  if (!r) {
    return 0
  }
  return ['prosperity', 'enrollment', 'prep'].reduce(
    (total, key) => total + readinessIssueCount(r[key]),
    0,
  )
}

/**
 * Three nearest non-archived experiences by start date, drafts included —
 * derived from the existing experience list response.
 */
export const getHomeExperienceCards = (
  quests,
  limit = HOME_EXPERIENCE_CARD_LIMIT,
) =>
  quests
    .map((quest) => ({ quest, scenario: getExperienceScenario(quest) }))
    .filter(({ scenario }) => scenario !== 'completed')
    .sort((a, b) => {
      const aStart = getProgramDatesRange(a.quest).start
      const bStart = getProgramDatesRange(b.quest).start
      if (!aStart) {
        return 1
      }
      if (!bStart) {
        return -1
      }
      return +new Date(aStart) - +new Date(bStart)
    })
    .slice(0, limit)

/** v7 "At a glance" — portfolio counts derived from the same list. */
export const getPortfolioStats = (quests) => {
  const scenarios = quests.map((quest) => getExperienceScenario(quest))
  return [
    {
      key: 'live',
      label: 'In progress',
      total: scenarios.filter((s) => s === 'live').length,
    },
    {
      key: 'upcoming',
      label: 'Upcoming',
      total: scenarios.filter((s) => s === 'upcoming').length,
    },
    {
      key: 'draft',
      label: 'Draft',
      total: scenarios.filter((s) => s === 'draft' || s === 'new').length,
    },
    {
      key: 'completed',
      label: 'Completed',
      total: scenarios.filter((s) => s === 'completed').length,
    },
  ]
}

/**
 * v7 "Needs attention" rows. Only items with a useful next action, so a row
 * appears when an experience is still a draft or reports readiness issues.
 * Sorted by nearest start date (live first), limited to three — same as the
 * Current + upcoming cards.
 */
export const getHomeAttentionItems = (quests, limit = HOME_ATTENTION_LIMIT) => {
  const rows = []
  quests.forEach((quest) => {
    const scenario = getExperienceScenario(quest)
    if (scenario === 'completed') {
      return
    }
    const name = getExperienceDisplayName(quest)
    const issues = countReadinessIssues(quest)

    if (scenario === 'draft' || scenario === 'new') {
      rows.push({
        key: quest._id || quest.id,
        quest,
        scenario,
        tone: 'warn',
        icon: issues > 0 ? String(issues) : '!',
        title: `${name} · cannot publish yet`,
        detail:
          issues > 0
            ? issues === 1
              ? '1 item remains in the draft.'
              : `${issues} items remain in the draft.`
            : 'Finish the draft before it can be published.',
        action: 'Continue →',
      })
      return
    }

    if (issues > 0) {
      const { start } = getProgramDatesRange(quest)
      const away = start
        ? moment.utc(start).diff(moment.utc().startOf('day'), 'days')
        : null
      rows.push({
        key: quest._id || quest.id,
        quest,
        scenario,
        tone: scenario === 'live' ? 'danger' : 'warn',
        icon: String(issues),
        title:
          scenario === 'live'
            ? `${name} · departure prep`
            : away != null && away > 0
            ? `${name} · ${away} day${away === 1 ? '' : 's'} away`
            : name,
        detail:
          issues === 1
            ? '1 readiness item still needs attention.'
            : `${issues} readiness items still need attention.`,
        action: scenario === 'live' ? 'Fix →' : 'Review →',
      })
    }
  })

  const scenarioRank = { live: 0, upcoming: 1, draft: 2, new: 2 }
  return rows
    .sort((a, b) => {
      const rankDiff =
        (scenarioRank[a.scenario] ?? 3) - (scenarioRank[b.scenario] ?? 3)
      if (rankDiff !== 0) {
        return rankDiff
      }
      const aStart = getProgramDatesRange(a.quest).start
      const bStart = getProgramDatesRange(b.quest).start
      if (!aStart) {
        return 1
      }
      if (!bStart) {
        return -1
      }
      return +new Date(aStart) - +new Date(bStart)
    })
    .slice(0, limit)
}
